import { useCallback, useContext, useEffect, useState } from "react"

import { Link, useLocalSearchParams } from "expo-router"

import {
  Account,
  AccountAUMFee,
  AccountCollection,
  AccountData,
  ClientCollection,
  Connection,
  Connector,
  getHorizon,
} from "wealthmode-frontend/backend"
import { Box, Button, ButtonText, HStack, Heading, Text, VStack } from "wealthmode-frontend/gluestack/ui"
import { formatMoney, formatPercentage } from "wealthmode-frontend/util"

import { DetailField, DetailLink, ResourceDetail } from "@/components"
import { PercentField } from "@/components/controls"
import { IRRValues } from "@/components/widgets"
import { BackendContext } from "@/context"
import { confidenceColor } from "@/lib/irr"
import {
  ACCOUNT_DATA_LINKS,
  CONNECTION_STATUS_COLORS,
  CONNECTION_STATUS_LABELS,
  SYNC_STATE_COLORS,
  SYNC_STATE_LABELS,
} from "@/lib/labels"
import { formatRate, percentToRate, rateToPercent } from "@/lib/rate"

export default function AccountDetail() {
  const { client_id, account_id } = useLocalSearchParams<{
    client_id: string
    account_id: string
  }>()

  const backend = useContext(BackendContext)

  const [account, setAccount] = useState<Account>()
  // A reload can hand back the same model instance, which on its own would not
  // re-render the page.
  const [, setRevision] = useState(0)

  const load = useCallback(async () => {
    const clients = backend.collection("clients") as ClientCollection
    const client = clients.model(client_id)
    await client.fetch({ reload: true })

    const accounts = client.link("accounts")?.resolve() as AccountCollection | undefined
    if (!accounts) {
      return
    }

    const model = accounts.model(account_id)
    await model.fetch({ reload: true })

    setAccount(model)
    setRevision((revision) => revision + 1)
  }, [backend, client_id, account_id])

  useEffect(() => {
    load().catch((err) => {
      console.error("Error: " + err)
      throw err
    })
  }, [load])

  const fields: DetailField[] = []
  const links: DetailLink[] = []
  let aumFee: AccountAUMFee | null = null

  if (account) {
    const data = account.embed("data") as AccountData | null
    const connection = account.embed("connection") as Connection | null
    const connector = account.embed("connector") as Connector | null
    aumFee = account.embed("aum_fee") as AccountAUMFee | null

    fields.push(
      {
        label: "Connector",
        value: connector ? (
          <Link href={`/connectors/${connector.id}`}>
            <Text className="text-markdown-link hover:opacity-80 underline">{connector.attributes.name}</Text>
          </Link>
        ) : (
          ""
        ),
      },
      { label: "Mask", value: account.attributes.mask },
      { label: "Type", value: account.attributes.type },
      { label: "Sub Type", value: account.attributes.subtype },
      { label: "Variant", value: account.attributes.variant ?? "--" },
      { label: "Balance", value: formatMoney(account.attributes.balance) },
    )

    // What the holdings are modelled to return over ten years, against the IRR
    // below, which is what the account did over the history it has.
    const blended = account.attributes.blended_cagr
    fields.push({
      label: "Blended 10Y CAGR",
      value: blended == null ? "--" : formatPercentage(getHorizon(blended, 10)),
    })

    // A depository account is not invested, so the market says nothing about it.
    fields.push(
      {
        label: "IRR",
        value: (
          <IRRValues
            irr={account.attributes.irr}
            plain={account.isDepository}
          />
        ),
      },
      {
        label: "Confidence",
        value: (
          <Text className={confidenceColor(account.attributes.value_confidence)}>
            {account.attributes.value_confidence == null ? "--" : formatPercentage(account.attributes.value_confidence)}
          </Text>
        ),
      },
    )

    if (aumFee) {
      const { rate, override_rate } = aumFee.attributes
      fields.push({
        label: "AUM Fee",
        value:
          rate === null ? "--" : `${formatRate(rate)} · ${override_rate === null ? "from connector" : "from override"}`,
      })
    }

    if (connection == null) {
      fields.push({ label: "Connection", value: "--" })
    } else {
      // Status is derived from every account on the connection, but only this
      // one is loaded here, so it is the account's own view of the connection.
      const { status } = connection.status([account])

      fields.push({
        label: "Connection",
        value: <Text className={CONNECTION_STATUS_COLORS[status]}>{CONNECTION_STATUS_LABELS[status]}</Text>,
      })
    }

    if (data == null) {
      fields.push({ label: "Status", value: "--" })
    } else {
      const state = data.syncState()

      fields.push({
        label: "Status",
        value: <Text className={SYNC_STATE_COLORS[state]}>{SYNC_STATE_LABELS[state]}</Text>,
      })
    }

    const base = `/clients/${client_id}/accounts/${account_id}`
    for (const { rel, title } of ACCOUNT_DATA_LINKS) {
      if (data?.link(rel)) {
        links.push({ href: `${base}/${rel}`, label: title })
      }
    }
  }

  return (
    <ResourceDetail
      title={account?.attributes.name || "Account"}
      subtitle={account_id}
      loading={!account}
      fields={fields}
      links={links}
    >
      {aumFee ? (
        <AUMFeeOverrideForm
          aumFee={aumFee}
          onSaved={load}
        />
      ) : null}
    </ResourceDetail>
  )
}

function AUMFeeOverrideForm({ aumFee, onSaved }: { aumFee: AccountAUMFee; onSaved: () => Promise<void> }) {
  const [percent, setPercent] = useState(() => rateToPercent(aumFee.attributes.override_rate))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setSaving(true)
    setError(null)

    try {
      await aumFee.setOverrideRate(percentToRate(percent))
      setPercent(rateToPercent(aumFee.attributes.override_rate))
      await onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <VStack className="gap-4">
      <VStack>
        <Heading size="md">AUM fee override</Heading>
        <Text className="text-xs text-foreground-secondary">
          Wins over the rate the connector assigns this account ({formatRate(aumFee.attributes.connector_rate)}), and no
          sync changes it. Blank clears it.
        </Text>
      </VStack>

      <PercentField
        label="Override"
        value={percent}
        onChange={setPercent}
      />

      {error ? (
        <Box className="rounded-md border border-border-medium bg-background-tertiary px-3 py-2">
          <Text className="text-sm text-foreground-secondary whitespace-pre-wrap">{error}</Text>
        </Box>
      ) : null}

      <HStack>
        <Button
          action="primary"
          className="bg-background-contrast"
          isDisabled={saving}
          onPress={() => void save()}
        >
          <ButtonText className="text-foreground-contrast">{saving ? "Saving…" : "Save"}</ButtonText>
        </Button>
      </HStack>
    </VStack>
  )
}
